// src/init.ts
import type { LoggerOptions, LogContext } from "./core/types";
import { Logger } from "./core/logger";
import { getRuntimeContext, getEnvVar } from "./runtime";
import {
  DEFAULT_OPTIONS,
  ENV,
  LOG_LEVELS,
  type LogLevel,
} from "./core/constants";
import type { ErrorHandler } from "./core/errors";
import { ConfigurationError } from "./core/errors";

// Add debug logging
const DEBUG = true;
function debugLog(...args: any[]) {
  if (DEBUG) {
    console.log("[INIT_DEBUG]", ...args);
  }
}

interface InitOptions {
  serviceName?: string;
  reporters?: LoggerOptions["reporters"];
  errorHandler?: ErrorHandler;
  context?: LogContext;
  formatOptions?: LoggerOptions["formatOptions"];
  level?: number;
}

let loggerInstance: Logger | null = null;

function validateOptions(options: InitOptions) {
  if (options.level !== undefined && (options.level < 0 || options.level > 5)) {
    throw new ConfigurationError("Log level must be between 0 and 5");
  }
}

/**
 * Initialize the logger synchronously with basic configuration
 */
export function initLogger(options: InitOptions = {}): Logger {
  debugLog("initLogger called with options:", JSON.stringify(options, null, 2));
  validateOptions(options);

  const runtimeContext = getRuntimeContext();
  debugLog("Runtime context:", runtimeContext);

  // Improve context handling to support direct serviceName
  if (
    options.serviceName &&
    (!options.context || !options.context.serviceName)
  ) {
    options.context = {
      ...(options.context || {}),
      serviceName: options.serviceName,
    };
    debugLog("Added serviceName to context:", options.context);
  }

  // Create default context with runtime information
  const defaultContext: LogContext = {
    environment: runtimeContext.environment as any,
    runtime: runtimeContext.runtime,
    serviceName: "default-service",
  };
  debugLog("Default context:", defaultContext);

  const mergedOptions: LoggerOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
    context: {
      ...defaultContext,
      ...options.context,
    },
    formatOptions: {
      ...DEFAULT_OPTIONS.formatOptions,
      ...options.formatOptions,
    },
  };
  debugLog("Merged options:", JSON.stringify(mergedOptions, null, 2));

  // Ensure reporters are properly set
  if (options.reporters) {
    debugLog("Setting reporters from options:", options.reporters);
    mergedOptions.reporters = options.reporters;
  }
  debugLog("Final reporters:", mergedOptions.reporters);

  // Create new logger instance with error handler
  debugLog("Creating logger instance");
  loggerInstance = Logger.getInstance(mergedOptions);

  if (!loggerInstance) {
    throw new Error("Failed to create logger instance");
  }

  // Set log level
  const level = options.level ?? detectLogLevel();
  debugLog("Setting log level:", level);
  loggerInstance.setLevel(level);

  debugLog("Logger instance created successfully");
  return loggerInstance;
}

/**
 * Get the current logger instance or create a new one with default options
 */
export function getLogger(): Logger {
  debugLog("getLogger called, loggerInstance exists:", !!loggerInstance);
  if (!loggerInstance) {
    return initLogger();
  }
  return loggerInstance;
}

/**
 * Create a new logger with additional context
 */
export function createLogger(context: LogContext): Logger {
  debugLog(
    "createLogger called with context:",
    JSON.stringify(context, null, 2)
  );
  return getLogger().withContext(context);
}

// Helper function to detect appropriate log level
function detectLogLevel(): number {
  const envLogLevel = getEnvVar("LOG_LEVEL") as LogLevel | undefined;
  if (envLogLevel && envLogLevel in LOG_LEVELS) {
    return LOG_LEVELS[envLogLevel];
  }

  const runtimeContext = getRuntimeContext();
  switch (runtimeContext.environment) {
    case ENV.production:
      return LOG_LEVELS.INFO;
    case ENV.test:
      return LOG_LEVELS.ERROR;
    case ENV.development:
    default:
      return LOG_LEVELS.DEBUG;
  }
}
